// No imports, deliberately.
//
// This module is read by three consumers that cannot share a type: the registrar
// composes it into raw SQL, the API compares it against Prisma's `ActivityStatus`
// enum, and Oak compares it against the *GraphQL* `ActivityStatus` enum. Those
// last two are different nominal TypeScript types even though their members are
// identical strings, so the shared answer has to be plain strings — a
// `readonly string[]` accepts members of either enum, while typing it as either
// one would lock the other out.
//
// The lists are exhaustive over `ActivityStatus`, and `stepStatuses.test.ts`
// asserts exactly that against the generated enum, so a ninth member fails the
// build here rather than quietly meaning different things in three places.

/**
 * Step statuses that mean "this step needs no further work".
 *
 * The complement, {@link OPEN_STEP_STATUSES}, is what "still needs someone" means.
 * Both matter to more than one writer: the registrar's reconcile closes and
 * reopens a stub's Staff step against them, `advanceCustomWorkflow` derives a
 * deliverable's status from them, and the detail page decides which verbs a step
 * offers by them. Three copies of a list this load-bearing is three chances for
 * a new `ActivityStatus` to mean different things in different places — reconcile
 * would report zero drift while stubs sat permanently stale.
 */
export const TERMINAL_STEP_STATUSES: readonly string[] = [
  "Completed",
  "StaffCompleted",
  "Canceled",
  "Skipped",
];

/** Step statuses that still need someone to do something. */
export const OPEN_STEP_STATUSES: readonly string[] = [
  "NotStarted",
  "InProgress",
  "Error",
  "Queued",
];

/** True when a step is finished, whatever flavour of `ActivityStatus` it is. */
export function isTerminalStepStatus(status: string): boolean {
  return TERMINAL_STEP_STATUSES.includes(status);
}
